import { memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeNavigationAction, selectNavigationAction } from "../../../Header/redux/actions";
import {
  isNavigationLoading,
  selectActiveLevel0Id,
  selectLevel0NavNodes,
} from "../../../Header/redux/navigationSelectors";
import { sendNavigationLinkEvent } from "../../Analytics/ga/ga";
import classNames from "classnames";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { type NavigationNode } from "../../../Header/types";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { ClickAwayListener } from "@mui/material";
import { NavigationFlyout } from "./NavigationFlyout";
import { useTranslation } from "react-i18next";
import { redirectToUrl } from "~/utils/url/urlUtils";

/**
 * Contains:
 * * Visible lvl 0 navigation
 * * Category navigation
 */
export const DesktopCategoryNavigation = memo(() => {
  const { val: isOpen, setFalse: close, setTrue: open } = useBooleanState(false);

  const dispatch = useDispatch();
  const openNavigation = (navItem: NavigationNode) => {
    dispatch(selectNavigationAction(navItem));
    open();
  };

  const closeNavigation = () => {
    dispatch(closeNavigationAction());
    close();
  };

  const selectedId = useSelector(selectActiveLevel0Id);
  const items = useSelector(selectLevel0NavNodes);
  const isLoading = useSelector(isNavigationLoading);

  return (
    <ClickAwayListener onClickAway={closeNavigation}>
      <nav className="scha__nav--0">
        {isLoading && <CircleSpinner className={"my-auto"} />}
        {!isLoading &&
          items.map((item, index) => (
            <Level0NavItem
              key={index}
              index={index}
              item={item}
              isActive={selectedId === item.id}
              onMouseClick={() => {
                if (selectedId === item.id) {
                  closeNavigation();
                } else {
                  openNavigation(item);
                }
              }}
            />
          ))}

        <NavigationFlyout open={isOpen} onClose={closeNavigation} />
      </nav>
    </ClickAwayListener>
  );
});

interface Level0NavItemProps {
  index: number;
  item: NavigationNode;
  isActive: boolean;

  onMouseClick: () => void;
}

const Level0NavItem = memo(({ index, item, isActive, onMouseClick }: Level0NavItemProps) => {
  const { t } = useTranslation();
  const onClick = useCallback(() => {
    onMouseClick();
    sendNavigationLinkEvent(item.gaLabel, 0);
  }, [onMouseClick, item]);

  const goToUrl = () => {
    if (item.url) {
      redirectToUrl(item.url);
    }
  };

  const navItemCls = classNames("scha__nav__item scha__nav__item--0", {
    active: isActive,
  });

  return (
    <li
      data-test-id={`header-navigation-l0-${index}`}
      onClick={window.navigationHover ? goToUrl : onClick}
      onMouseEnter={window.navigationHover ? onClick : undefined}
      className={navItemCls}
    >
      <div className="wrapper">{t(item.title)}</div>
    </li>
  );
});
